import { ContentState } from "draft-js";
import { List } from "immutable";
import { Observable, WritableObservable } from "micro-observables";
import { Language } from "@domain/currentProfile/currentProfile";
import { Category, SubCategory } from "@domain/lms/category/category";
import { Author } from "@domain/lms/content";
import { ContentLevel } from "@domain/lms/contentLevel";
import { CourseStatus } from "@domain/lms/course/courseDraft";
import { ExerciseType } from "@domain/lms/course/exercise/exercise";
import { ExerciseChoiceDraft, ExerciseDraft } from "@domain/lms/course/exercise/exerciseDraft";
import { isExerciseValid } from "@domain/lms/course/exercise/utils";
import { QuizDraft } from "@domain/lms/quiz/quizDraft";
import { ContentVisibility } from "@domain/lms/visibility";
import { withId } from "@utils/domain";
import { ExerciseController } from "@ui/pages/lms/courseEditorPage/exerciseEditor/exerciseController";

export class QuizEditorController {
	private previousValues: QuizDraft[];
	private readonly currentValueIndex = new WritableObservable(0);
	readonly quiz: Observable<QuizDraft>;

	constructor(initialQuizDraft: QuizDraft) {
		this.previousValues = [initialQuizDraft];
		this.quiz = this.currentValueIndex.transform((index) => this.previousValues[index]);
	}

	updateQuiz(quizUpdate: Partial<QuizDraft>): void {
		const quiz: QuizDraft = { ...this.quiz.get(), ...quizUpdate };
		this.previousValues = [...this.previousValues.slice(0, this.currentValueIndex.get() + 1), quiz];
		this.currentValueIndex.set(this.currentValueIndex.get() + 1);
	}

	setTitle(title: string): void {
		this.updateQuiz({ title });
	}
	setDescription(description: ContentState): void {
		this.updateQuiz({ description });
	}
	setCategory(category?: Category): void {
		this.updateQuiz({ category });
	}
	setLevel(level?: ContentLevel): void {
		this.updateQuiz({ level });
	}
	setSubCategory(subCategory?: SubCategory): void {
		this.updateQuiz({ subCategory });
	}
	setLanguage(language: Language): void {
		this.updateQuiz({ language });
	}
	setStatus(status: CourseStatus): void {
		this.updateQuiz({ status });
	}
	setPublisher(publisher?: Author): void {
		this.updateQuiz({ publisher });
	}

	setOwnership(keepOwnership?: boolean): void {
		this.updateQuiz({ keepOwnership });
	}

	setVisibility(visibility: ContentVisibility): void {
		this.updateQuiz({ visibility });
	}

	setColor(color: string): void {
		this.updateQuiz({ color });
	}

	toggleWorkspace(id: string): void {
		const quiz = this.quiz.get();
		const workspaceIds = quiz.workspaceIds.update((ids) => (ids.has(id) ? ids.remove(id) : ids.add(id)));
		this.updateQuiz({ workspaceIds });
	}

	private updateExercise(id: string, update: (current: ExerciseDraft) => ExerciseDraft): void {
		this.updateQuiz({ exercises: this.quiz.get().exercises.map((c) => (c.id === id ? update(c) : c)) });
	}

	private removeExercise(id: string): void {
		const quiz = this.quiz.get();
		const index = quiz.exercises.findIndex((s) => s.id === id);
		if (index < 0) {
			return;
		}
		this.updateQuiz({ exercises: quiz.exercises.remove(index) });
	}

	addExercise(): void {
		this.updateQuiz({
			exercises: this.quiz.get().exercises.push(
				withId({
					title: "",
					type: "UNIQUE" as ExerciseType,
					choices: List([withId({ text: "", valid: false }) as ExerciseChoiceDraft]),
					comment: ContentState.createFromText(""),
					editing: true,
				})
			),
		});
	}

	exerciseController(exercise: ExerciseDraft): ExerciseController {
		return new ExerciseController(
			(updater) => this.updateExercise(exercise.id, updater),
			() => this.removeExercise(exercise.id)
		);
	}

	isValidForDraft(): boolean {
		const quiz = this.quiz.get();
		return quiz.title.length > 0;
	}

	isValidForNextStep(): boolean {
		const quiz = this.quiz.get();
		if (!quiz.title) {
			return false;
		}
		if (!quiz.exercises.every((ex) => isExerciseValid(ex))) {
			return false;
		}
		if (quiz.visibility === "PRIVATE" && quiz.workspaceIds.size < 1) {
			return false;
		}
		return true;
	}

	isValidForPublication(): boolean {
		const quiz = this.quiz.get();
		if (!quiz.category || !quiz.description.getPlainText().trim()) {
			return false;
		}
		return this.isValidForNextStep();
	}
}
